import { AnyAction, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { showError } from '@app/utils/message';
import { OrderDataArray, OrderItem, OrderModel, OrderQueryParams, ProductItemsProp } from '@app/interfaces/order';
import { combineOrderApi, createOrderApi, deleteOrderApi, getOrderApi, getOrdersApi, printCombineOrderApi, removeImageOrderApi, sendZaloMessageApi, transferCustomerApi, updateOrderApi, updateRemarkApi, updateStatusApi, uploadImageOrderApi } from '@app/api/order.api';

export interface OrderState {
  data: OrderModel[];
  totalRows: number;
  pagination: {
    current: number,
    pageSize: number
  },
  filters: any,
  sorter: any,
  searchTerm: string;
  selectedRecord: OrderItem[];
  loading: boolean;
  error: string | null;
  productItems: ProductItemsProp[];
  record: OrderModel | null;
  /** requestId của getOrders gần nhất — response của request cũ về muộn thì bỏ qua. */
  listRequestId?: string;
}

const initialState: OrderState = {
  data: [],
  totalRows: 0,
  pagination: { current: 1, pageSize: 20 },
  filters: {},
  sorter: {},
  searchTerm: "",
  selectedRecord: [],
  loading: false,
  error: null,
  productItems: [],
  record: null,
};

interface ResponseData {
  data: OrderItem;
  message: string;
  error: string;
}

// Thunks
export const getOrders = createAsyncThunk<OrderDataArray, OrderQueryParams>(
  'order/getOrders',
  async (params: OrderQueryParams, { rejectWithValue }) => {
    try {
      const orders = (await getOrdersApi(params)) as OrderDataArray;
      return orders || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const getOrder = createAsyncThunk<OrderModel, number>(
  'order/getOrder',
  (async (orderId: number, { rejectWithValue }: any) => {
    try {
      const order = await getOrderApi(orderId);
      return order;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred'
      );
    }
  }) as any
);

export const createOrder = createAsyncThunk<ResponseData, any>('order/createOrder', async (fromData) => {
  const response = await createOrderApi(fromData);
  return response;
});

export const updateOrder = createAsyncThunk<ResponseData, { orderId: number; formData: any }>(
  'order/updateOrder',
  async ({ orderId, formData }) => {
    const response = await updateOrderApi(orderId, formData);
    return response;
  },
);

export const deleteOrder = createAsyncThunk<{ orderId: number; response: any }, number>(
  'order/deleteOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await deleteOrderApi(orderId);
      return { orderId, response };
    } catch (error) {
      // Handle errors with rejectWithValue
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const updateStatusOrder = createAsyncThunk<ResponseData, { orderId: number; formData: any }>(
  'order/updateStatusOrder',
  async ({ orderId, formData }) => {
    const response = await updateStatusApi(orderId, formData);
    return response;
  },
);

export const updateRemark = createAsyncThunk<ResponseData, { orderId: number; remark: string }>(
  'order/updateRemark',
  async ({ orderId, remark }) => {
    const response = await updateRemarkApi(orderId, remark);
    return response;
  },
);

export const uploadImageOrder = createAsyncThunk<ResponseData, { orderId: number; formData: any }>(
  'order/uploadImageOrder',
  async ({ orderId, formData }) => {
    const response = await uploadImageOrderApi(orderId, formData);
    return response;
  },
);

export const sendZaloMessage = createAsyncThunk<ResponseData, { orderId: number }>(
  'order/sendZaloMessage',
  async ({ orderId }) => {
    const response = await sendZaloMessageApi(orderId);
    return response;
  },
);

export const transferCustomerOrder = createAsyncThunk<ResponseData, { orderId: number, formData: any }>(
  'order/transferCustomerOrder',
  async ({ orderId, formData }) => {
    const response = await transferCustomerApi(orderId, formData);
    return response;
  },
);

export const combineOrder = createAsyncThunk<ResponseData, { orderId: number, formData: any }>(
  'order/combineOrder',
  async ({ orderId, formData }) => {
    const response = await combineOrderApi(orderId, formData);
    return response;
  },
);

export const printCombineOrder = createAsyncThunk<ResponseData, { orderId: number }>(
  'order/printCombineOrder',
  async ({ orderId }) => {
    const response = await printCombineOrderApi(orderId);
    return response;
  },
);

export const removeImageOrder = createAsyncThunk<ResponseData, { orderId: number; imageId: number }>(
  'order/removeImageOrder',
  async ({ orderId, imageId }) => {
    const response = await removeImageOrderApi(orderId, imageId);
    return response;
  },
);

const updateStateWithData = (state: any, data: any, newMessage: string) => {
  const updatedIndex = state.data.findIndex((item: any) => item.id === Number(data.id));
  if (updatedIndex !== -1) {
    state.data[updatedIndex] = data.attributes;
    state.record = data.attributes;
    message.success(newMessage);
  }
};

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

// Slice
export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      if (pagination !== undefined) state.pagination = pagination;
      if (filters !== undefined) state.filters = filters;
      if (sorter !== undefined) state.sorter = sorter;
      if (searchTerm !== undefined) state.searchTerm = searchTerm;
    },
    addProductItem: (state, action: PayloadAction<ProductItemsProp>) => {
      const item = action.payload;
      state.productItems.unshift(item);
      message.success(`Sản phẩm ${item.name}-${item.product_code} thêm thành công.`);
    },
    addProductItems: (state, action: PayloadAction<ProductItemsProp[]>) => {
      const items = action.payload;
      state.productItems = items;
    },
    updateProductItem: (state, action: PayloadAction<ProductItemsProp>) => {
      const index = state.productItems.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        const item = action.payload
        state.productItems[index] = action.payload;
        //message.success(`Sản phẩm ${item.name}-${item.product_code} cập nhật thành công.`)
      }
    },
    deleteProductItem: (state, action: PayloadAction<string>) => {
      const recordId = action.payload;
      state.productItems = state.productItems.filter(item => item.id !== recordId);
      message.success(`Sản phẩm ${recordId} xoá thành công.`)
    },
    resetProductItems: (state, _) => {
      return {...state, productItems: []}
    }
  },
  extraReducers: (builder) => {
    builder
      // Mỗi phím gõ ở ô tìm kiếm bắn một request; từ khoá ngắn khớp nhiều đơn nên chạy
      // chậm và có thể về sau cùng, ghi đè kết quả đúng. Chỉ nhận request mới nhất.
      .addCase(getOrders.pending, (state, action) => {
        state.listRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrders.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;

        state.loading = false;
        state.error = (action.payload as string) ?? action.error.message ?? null;
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;

        const { rows, total_rows } = action.payload;

        const data = rows;
        state.data = data.map((item: OrderItem) => item.attributes) || [];

        state.totalRows = parseInt(total_rows, 10);
        state.loading = false;
      })
      .addCase(getOrder.fulfilled, (state, action) => {
        const order = action.payload;
        state.record = order;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.data.unshift(data.attributes);
       // message.success(newMessage);
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        const orderId = typeof data.id === 'string' ? parseInt(data.id, 10) : data.id;

        const updatedIndex = state.data.findIndex((item) => item.id === orderId.toString());
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data.attributes;
          state.record = data.attributes;
          message.success(newMessage);
        }
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        const { orderId, response } = action.payload;

        if (response.error) { 
          showError(response.error); return;
        }
        else {
          state.data = state.data.filter((order) => order.id !== orderId.toString());
          message.success(response.message);
        }
      })
      .addCase(updateStatusOrder.fulfilled, (state, action) => {
        const { data, message: newMessage, error } = action.payload;

        if (error) {
          showError(error);
        }
        else {
          try {
            const updatedIndex = state.data.findIndex((item) => {
              return Number(item.id) === Number(data.id);
            });

            if (updatedIndex !== -1) {
              state.data[updatedIndex] = data.attributes;
              state.record = data.attributes;
            } else {
              console.warn('Order not found in state after status update:', data.id);
            }
          } catch (err) {
            console.error('Error updating order state:', err);
          }
          // Always show success message if no error
          message.success(newMessage);
        }
      })
      .addCase(updateStatusOrder.rejected, (state, action) => {
        const errorMessage = action.error.message || 'Failed to update order status';
        showError(errorMessage);
      })
      .addCase(updateRemark.fulfilled, (state, action) => {
        const { data, message: newMessage, error } = action.payload;

        if (error) {
          showError(error);
        } else {
          try {
            const updatedIndex = state.data.findIndex((item) => {
              return Number(item.id) === Number(data.id);
            });

            if (updatedIndex !== -1) {
              state.data[updatedIndex] = data.attributes;
              state.record = data.attributes;
            } else {
              console.warn('Order not found in state.data after remark update:', data.id);
            }
          } catch (err) {
            console.error('Error updating remark in state:', err);
          }
          message.success(newMessage);
        }
      })
      .addCase(updateRemark.rejected, (state, action) => {
        const errorMessage = action.error.message || 'Failed to update remark';
        showError(errorMessage);
      })
      .addCase(uploadImageOrder.fulfilled, (state, action) => {
        const { data, message: newMessage, error } = action.payload;
        if (error) {
          showError(error);
        }
        else {
          if (!state.record || !Array.isArray(state.record.images)) return;
          state.record.images = Array.isArray(data) ? data : [];
          message.success(newMessage);
        }
      })
      .addCase(removeImageOrder.fulfilled, (state, action) => {
        const { data, message: newMessage, error } = action.payload;

        if (error) {
          showError(error);
        }
        else {
          if (!state.record || !Array.isArray(state.record.images)) return;
          state.record.images = state.record.images.filter(item => item.id !== Number(data.id));

          message.success(newMessage);
        }
      })
      .addCase(sendZaloMessage.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        updateStateWithData(state, data, newMessage);
      })
      .addCase(transferCustomerOrder.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        updateStateWithData(state, data, newMessage);
      })
      .addCase(combineOrder.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        updateStateWithData(state, data, newMessage);
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        const errorText =
          (typeof action.payload === 'string' ? action.payload : null) ||
          action.payload?.message ||
          action.payload?.error ||
          action?.error?.message;
        if (errorText) {
          showError(errorText);
        }
      });
  },
});

export const {
  updateTableParams,
  addProductItem,
  updateProductItem,
  deleteProductItem,
  resetProductItems,
  addProductItems
} = orderSlice.actions;

export default orderSlice.reducer;
